/**
 * DIVETTA MILANO R3 — header orchestration only.
 * Horizon owns its menu drawer and predictive search internals; this bridge
 * connects the visible header controls while preserving native route fallbacks.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ld-modern-header]';
  const controllers = new WeakMap();

  const waitForDefinition = (name, timeout = 900) => Promise.race([
    customElements.whenDefined(name).then(() => true),
    new Promise((resolve) => window.setTimeout(() => resolve(false), timeout)),
  ]);

  function destroy(root) {
    const controller = controllers.get(root);
    if (!controller) return;
    controller.abort.abort();
    controller.unsubscribeScroll?.();
    if (controller.frame) cancelAnimationFrame(controller.frame);
    controllers.delete(root);
    root.removeAttribute('data-ld-modern-ready');
  }

  function init(root) {
    if (!(root instanceof HTMLElement)) return;
    destroy(root);

    const abort = new AbortController();
    const { signal } = abort;
    const state = { abort, frame: 0 };
    const navItems = Array.from(root.querySelectorAll('[data-ld-nav-item]'));
    const drawerTrigger = root.querySelector('[data-ld-drawer-trigger]');
    const drawerHost = root.querySelector('header-drawer');
    const drawerDetails = root.querySelector('#Details-menu-drawer-container');
    const drawerSummary = drawerDetails?.querySelector(':scope > summary');
    const searchTriggers = Array.from(root.querySelectorAll('[data-ld-search-trigger]'));
    const cartTriggers = Array.from(root.querySelectorAll('[data-ld-r3-cart-trigger]'));

    controllers.set(root, state);
    root.dataset.ldModernReady = 'true';

    const readCartCount = (value) => {
      if (value == null || String(value).trim() === '') return null;
      const count = Number(value);
      return Number.isInteger(count) && count >= 0 ? count : null;
    };

    document.addEventListener('cart:update', (event) => {
      let count = readCartCount(event.detail?.resource?.item_count);
      if (count === null) {
        // Add events carry a quantity delta; the returned Shopify section
        // contains the absolute count, including items already in the cart.
        for (const html of Object.values(event.detail?.data?.sections || {})) {
          if (typeof html !== 'string') continue;
          const section = new DOMParser().parseFromString(html, 'text/html');
          count = readCartCount(section.querySelector('[ref="cartItemCount"]')?.textContent);
          if (count !== null) break;
        }
      }
      if (count === null) return;
      root.querySelectorAll('[data-ld-r3-cart-count]').forEach((badge) => {
        badge.textContent = String(count);
        badge.classList.toggle('is-empty', count === 0);
      });
    }, { signal });

    const syncScroll = () => {
      state.frame = 0;
      const top = Math.max(window.scrollY, document.documentElement.scrollTop || 0);
      root.classList.toggle('is-scrolled', top > 10);
    };

    const requestScrollSync = () => {
      if (state.frame) return;
      state.frame = requestAnimationFrame(syncScroll);
    };

    let lastMenuState = null;
    const emitMenuState = () => {
      const open = Boolean(drawerDetails?.open) || navItems.some((item) => item.classList.contains('is-open'));
      if (lastMenuState === open) return;
      lastMenuState = open;
      document.dispatchEvent(new CustomEvent('ld:menu-state', {
        detail: { open, source: drawerDetails?.open ? 'header-drawer' : 'header-navigation' },
      }));
    };

    const closeNav = (except = null) => {
      navItems.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-open');
        item.querySelector('[data-ld-nav-toggle]')?.setAttribute('aria-expanded', 'false');
      });
      requestAnimationFrame(() => { if (!signal.aborted) emitMenuState(); });
    };

    navItems.forEach((item) => {
      const toggle = item.querySelector('[data-ld-nav-toggle]');
      if (!(toggle instanceof HTMLButtonElement)) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const willOpen = !item.classList.contains('is-open');
        closeNav(item);
        item.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', String(willOpen));
        emitMenuState();
      }, { signal });
    });

    const syncDrawer = () => {
      const open = Boolean(drawerDetails?.open);
      drawerTrigger?.setAttribute('aria-expanded', String(open));
      emitMenuState();
    };

    const closeDrawer = async () => {
      if (!drawerDetails?.open) return;
      if (typeof drawerHost?.close === 'function') {
        await drawerHost.close();
      } else {
        drawerDetails.removeAttribute('open');
        drawerDetails.classList.remove('menu-open');
        drawerSummary?.setAttribute('aria-expanded', 'false');
        document.documentElement.removeAttribute('scroll-lock');
      }
      syncDrawer();
    };

    if (drawerTrigger instanceof HTMLElement && drawerSummary instanceof HTMLElement) {
      drawerTrigger.addEventListener('click', async (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        closeNav();

        if (!customElements.get('header-drawer')) {
          await waitForDefinition('header-drawer', 700);
        }

        if (signal.aborted) return;
        if (!root.isConnected || !customElements.get('header-drawer')) {
          window.location.assign(drawerTrigger.href || window.Shopify?.routes?.root || '/');
          return;
        }
        drawerSummary.click();
        requestAnimationFrame(syncDrawer);
      }, { signal });

      drawerDetails?.addEventListener('toggle', syncDrawer, { signal });
      syncDrawer();
    }

    const openSearch = async (trigger) => {
      const modal = document.getElementById('search-modal');
      if (!modal) {
        window.location.assign(trigger.href);
        return;
      }

      await closeDrawer();
      await document.querySelector('cart-drawer-component')?.close?.();

      if (typeof modal.showDialog !== 'function') {
        await waitForDefinition('dialog-component');
      }

      if (signal.aborted || !root.isConnected) return;
      if (typeof modal.showDialog !== 'function') {
        window.location.assign(trigger.href);
        return;
      }

      const returnTarget = trigger.getClientRects().length ? trigger : searchTriggers.find((item) => item.getClientRects().length);
      returnTarget?.focus({ preventScroll: true });
      modal.showDialog();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const dialog = modal.querySelector('dialog');
          const activeDialog = document.activeElement?.closest('dialog');
          if (!signal.aborted && modal.isConnected && dialog?.open && (!activeDialog || activeDialog === dialog)) modal.querySelector('[ref="searchInput"]')?.focus();
        });
      });
    };

    searchTriggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLAnchorElement)) return;

      trigger.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        openSearch(trigger).catch(() => { if (!signal.aborted) window.location.assign(trigger.href); });
      }, { signal });

      const modal = document.getElementById('search-modal');
      modal?.addEventListener('dialog:open', () => trigger.setAttribute('aria-expanded', 'true'), { signal });
      modal?.addEventListener('dialog:close', () => {
        trigger.setAttribute('aria-expanded', 'false');
      }, { signal });
    });

    cartTriggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLAnchorElement)) return;
      const drawer = document.getElementById(trigger.getAttribute('aria-controls') || '');
      const syncCartState = (expanded) => trigger.setAttribute('aria-expanded', String(expanded));

      trigger.addEventListener('click', async (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (trigger.getAttribute('aria-controls') !== 'cart-drawer') return;
        event.preventDefault();
        await closeDrawer();
        await document.getElementById('search-modal')?.closeDialog?.();
        if (!customElements.get('cart-drawer-component')) await waitForDefinition('cart-drawer-component', 900);
        if (signal.aborted) return;
        const activeDrawer = document.getElementById('cart-drawer');
        if (!root.isConnected || typeof activeDrawer?.open !== 'function') {
          window.location.assign(trigger.href);
          return;
        }
        trigger.focus({ preventScroll: true });
        activeDrawer.open();
        syncCartState(true);
      }, { signal });

      drawer?.addEventListener('dialog:open', () => syncCartState(true), { signal });
      drawer?.addEventListener('dialog:close', () => {
        syncCartState(false);
      }, { signal });
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Node) || root.contains(event.target)) return;
      closeNav();
    }, { signal });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const focusedItem = navItems.find((item) => item.classList.contains('is-open') && item.contains(document.activeElement));
      if (focusedItem) {
        event.preventDefault();
        focusedItem.querySelector('[data-ld-nav-toggle]')?.focus({ preventScroll: true });
      }
      closeNav();
    }, { signal });

    root.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node) {
        if (!root.contains(event.relatedTarget)) closeNav();
        return;
      }
      requestAnimationFrame(() => {
        if (!signal.aborted && !root.contains(document.activeElement)) closeNav();
      });
    }, { signal });

    if (window.DivettaZorge?.subscribe) state.unsubscribeScroll = window.DivettaZorge.subscribe('header-modern-scroll', syncScroll);
    else window.addEventListener('scroll', requestScrollSync, { passive: true, signal });
    syncScroll();
  }

  function initAll(scope = document) {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement && event.target.matches(ROOT_SELECTOR)) destroy(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(destroy);
  });
})();
